import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, InputAdornment, Menu, MenuItem, Pagination, TextField, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import DeleteDialog from '../../../components/admin/common/CustomDeleteDialog';
import { showCustomComponentNotification, showErrorNotification } from '../../../components/admin/common/NotiService';
import OZDialog from '../../../components/admin/common/OZDialog';
import { PaginationRenderIcon } from '../../../components/admin/common/OZDataGrid/OZGridPagination';
import { enqueueCampaignAsyncAction, getCompletedCampaignsForDelete } from '../../../services/api-service';

const BULK_DELETE_MAX_CAMPAIGNS = 100;
const OUTBOUND_CAMPAIGN_TYPES = ['Predictive', 'Progressive', 'Preview', 'IVR'];
// Short labels for the per-type breakdown of the current selection.
const TYPE_ABBR = { Predictive: 'Pred', Progressive: 'Prog', Preview: 'Prev', IVR: 'IVR' };
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;
const LIST_HEIGHT = 480;
const ROW_HEIGHT = 48;
// Approximate non-list vertical space (dialog top offset, title, filters, search,
// count row, pagination and footer) reserved when fitting the list to the viewport.
const DIALOG_VERTICAL_CHROME = 470;

// Default "delete all data" payload for the async campaign delete API, mirroring
// the payload the single Reset Campaign dialog sends for delete_campaign_data.
const buildBulkDeletePayload = (campaignIds) => ({
    fromDate: '0000-00-00 00:00:00',
    toDate: '0000-00-00 00:00:00',
    status: 'All',
    dialStatus: 'All',
    disposition: 'All',
    skillId: 0,
    agentId: 0,
    priority: '',
    dateSortBy: '',
    returnCount: '0',
    reportName: 'delete_campaign_data',
    fileName: 'DeleteCampaignData',
    export_format: 'csv',
    compress: false,
    type: 1,
    campaignId: campaignIds,
});

// The bulk delete endpoint responds with { "message": "Process initiated" } on
// success (no job_id / status), so treat the "initiated" message as success too,
// while keeping the job_id / status checks for forward compatibility.
const isAsyncActionSuccess = (resp) => {
    if (!resp) {
        return false;
    }
    if (resp?.job_id) {
        return true;
    }
    const status = `${resp?.status || resp?.Status || ''}`.toLowerCase();
    if (status === 'queued' || status === 'success' || status === 'in_progress') {
        return true;
    }
    const message = `${resp?.message || resp?.Message || ''}`.toLowerCase();
    return message.includes('initiated');
};

// The apiID 76 response shape can vary; pull out an array of campaign rows defensively.
const extractCampaignRows = (resp) => {
    if (Array.isArray(resp)) {
        return resp;
    }
    const candidate = resp?.Data ?? resp?.data ?? resp?.campaigns ?? resp?.Campaigns ?? resp?.records ?? resp?.Records ?? resp?.result ?? resp?.Result;
    return Array.isArray(candidate) ? candidate : [];
};

// Normalise a raw row into { id, name, position, type }, tolerating PascalCase / camelCase / snake_case.
const normaliseCampaign = (row) => ({
    id: row?.campaignId ?? row?.CampaignId ?? row?.campaign_id ?? row?.id ?? row?.Id,
    name: row?.campaignName ?? row?.CampaignName ?? row?.campaign_name ?? row?.name ?? row?.Name ?? '',
    position: row?.position ?? row?.CampaignPosition ?? row?.campaign_position ?? row?.Position ?? '',
    type: row?.campaignType ?? row?.CampaignType ?? row?.campaign_type ?? row?.type ?? row?.Type ?? '',
});

const DeleteCampaignDataDialog = ({ open, onClose, ivrDialerV2Enabled = false, onDeleted }) => {
    const navigate = useNavigate();

    const availableTypes = useMemo(
        () => OUTBOUND_CAMPAIGN_TYPES.filter((type) => !(ivrDialerV2Enabled && type === 'IVR')),
        [ivrDialerV2Enabled]
    );

    const [loading, setLoading] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState(availableTypes);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [rowsPerPageMenuAnchor, setRowsPerPageMenuAnchor] = useState(null);
    const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);

    useEffect(() => {
        const handleResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        let active = true;
        const fetchCampaigns = async () => {
            setLoading(true);
            try {
                const resp = await getCompletedCampaignsForDelete(availableTypes.join(','));
                if (!active) {
                    return;
                }
                const rows = extractCampaignRows(resp)
                    .map(normaliseCampaign)
                    .filter((c) => c.id !== undefined && c.id !== null && `${c.name}`.length > 0)
                    // Defensive: drop IVR campaigns client-side when IVR Dialer V2 is enabled.
                    .filter((c) => !(ivrDialerV2Enabled && `${c.type}`.toUpperCase() === 'IVR'));
                setCampaigns(rows);
            } catch (e) {
                console.error('Failed to load completed campaigns', e);
                if (active) {
                    setCampaigns([]);
                    showErrorNotification('Unable to load campaigns. Please try again later.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };
        fetchCampaigns();
        return () => {
            active = false;
        };
    }, [availableTypes, ivrDialerV2Enabled]);

    const selectedCount = Object.keys(selected).length;
    const capReached = selectedCount >= BULK_DELETE_MAX_CAMPAIGNS;

    const filteredCampaigns = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        return campaigns.filter((c) => {
            const matchesType = selectedTypes.includes(c.type);
            if (!matchesType) {
                return false;
            }
            if (!searchText) {
                return true;
            }
            return (
                `${c.name}`.toLowerCase().includes(searchText) ||
                `${c.type}`.toLowerCase().includes(searchText)
            );
        });
    }, [campaigns, selectedTypes, search]);

    const totalPages = Math.max(1, Math.ceil(filteredCampaigns.length / rowsPerPage));

    const pageCampaigns = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredCampaigns.slice(start, start + rowsPerPage);
    }, [filteredCampaigns, page, rowsPerPage]);

    const hasActiveFilter = useMemo(() => {
        const searchActive = search.trim().length > 0;
        const typeFilterActive = selectedTypes.length !== availableTypes.length;
        return searchActive || typeFilterActive;
    }, [search, selectedTypes, availableTypes]);

    useEffect(() => {
        setPage(1);
    }, [search, selectedTypes, rowsPerPage]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    // Clear action remains scoped to the current searched/filtered list.
    const filteredSelectedCount = useMemo(
        () => filteredCampaigns.reduce((count, c) => (selected[c.id] ? count + 1 : count), 0),
        [filteredCampaigns, selected]
    );
    // Select-all checkbox is scoped to the current paginated search/filter page only.
    const pageSelectedCount = useMemo(
        () => pageCampaigns.reduce((count, c) => (selected[c.id] ? count + 1 : count), 0),
        [pageCampaigns, selected]
    );
    const allPageSelected = pageCampaigns.length > 0 && pageSelectedCount === pageCampaigns.length;
    const somePageSelected = pageSelectedCount > 0 && !allPageSelected;
    const clearableSelectedCount = hasActiveFilter ? filteredSelectedCount : selectedCount;

    // Breakdown of the current selection by campaign type, for the detail line under the counter.
    const selectedTypeCounts = useMemo(() => {
        const counts = {};
        OUTBOUND_CAMPAIGN_TYPES.forEach((type) => { counts[type] = 0; });
        Object.values(selected).forEach((campaign) => {
            const key = `${campaign?.type || ''}`;
            if (counts[key] !== undefined) {
                counts[key] += 1;
            }
        });
        return counts;
    }, [selected]);

    const toggleSelectAllPage = () => {
        const next = { ...selected };
        if (allPageSelected) {
            // Clear only the selected items present on the current paginated page.
            pageCampaigns.forEach((c) => { delete next[c.id]; });
            setSelected(next);
            return;
        }
        // Select every item on the current paginated page, respecting the max selection cap.
        let truncated = false;
        for (let i = 0; i < pageCampaigns.length; i += 1) {
            const campaign = pageCampaigns[i];
            if (next[campaign.id]) {
                continue;
            }
            if (Object.keys(next).length >= BULK_DELETE_MAX_CAMPAIGNS) {
                truncated = true;
                break;
            }
            next[campaign.id] = campaign;
        }
        setSelected(next);
        if (truncated) {
            showErrorNotification(`You can select a maximum of ${BULK_DELETE_MAX_CAMPAIGNS} campaigns at a time.`);
        }
    };

    const clearSelected = () => {
        if (!hasActiveFilter) {
            setSelected({});
            return;
        }
        const next = { ...selected };
        filteredCampaigns.forEach((c) => { delete next[c.id]; });
        setSelected(next);
    };

    const clearAllSelected = () => {
        setSelected({});
    };

    const toggleType = (type) => {
        setSelectedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
    };

    const toggleCampaign = (campaign) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[campaign.id]) {
                delete next[campaign.id];
            } else {
                if (Object.keys(next).length >= BULK_DELETE_MAX_CAMPAIGNS) {
                    return prev;
                }
                next[campaign.id] = campaign;
            }
            return next;
        });
    };

    const handleClose = () => {
        if (deleting) {
            return;
        }
        onClose?.();
    };

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = useCallback((event) => {
        setRowsPerPage(Number(event.target.value));
        setRowsPerPageMenuAnchor(null);
    }, []);

    const notifyDeleteSuccess = (message) => {
        showCustomComponentNotification({
            component: (
                <label style={{ fontSize: '14px', fontWeight: '400', color: '#212121' }}>
                    {message}
                    <br />
                    <span
                        style={{ textDecoration: 'none', cursor: 'pointer', color: '#3d8bf8', fontSize: '12px' }}
                        onClick={() => { navigate('/admin/reportStatusAdmin'); }}
                    >
                        Click here to Navigate to "Report Status" Page
                    </span>
                </label>
            ),
            variant: 'success',
        });
    };

    const handleDelete = async () => {
        const selectedList = Object.values(selected);
        if (selectedList.length === 0) {
            return;
        }
        try {
            setDeleting(true);
            const ids = selectedList.map((c) => c.id).filter((id) => id !== undefined && id !== null).join(',');
            const resp = await enqueueCampaignAsyncAction(buildBulkDeletePayload(ids));
            if (isAsyncActionSuccess(resp)) {
                const backendMessage = resp?.message || resp?.Message || 'Delete request submitted';
                notifyDeleteSuccess(`${backendMessage} for ${selectedList.length} selected campaign(s)${resp?.job_id ? ` (job id ${resp.job_id})` : ''}.`);
                onDeleted?.();
                onClose?.();
            } else {
                showErrorNotification(resp?.message || resp?.Message || 'Unable to queue delete request');
            }
        } catch (e) {
            console.error(e);
            showErrorNotification('Unable to queue delete request');
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
        }
    };

    const rowStyle = useMemo(
        () => ({ fontSize: '14px', fontWeight: 400, color: '#212121' }),
        []
    );
    const secondaryStyle = useMemo(
        () => ({ fontSize: '12px', fontWeight: 400, color: '#99A0A8' }),
        []
    );

    // Keep the dialog within the viewport: cap the list to the space left after the
    // dialog's top offset, header, filters, search, pagination and footer chrome.
    const availableListHeight = Math.max(ROW_HEIGHT * 3, viewportHeight - DIALOG_VERTICAL_CHROME);
    const listBoxHeight = Math.min(LIST_HEIGHT, availableListHeight);
    const listHeight = Math.min(listBoxHeight, Math.max(pageCampaigns.length, 1) * ROW_HEIGHT);

    const renderRow = ({ index, style }) => {
        const campaign = pageCampaigns[index];
        const isSelected = Boolean(selected[campaign.id]);
        const disabled = !isSelected && capReached;
        return (
            <div style={style} key={campaign.id}>
                <Tooltip title={disabled ? `You can select a maximum of ${BULK_DELETE_MAX_CAMPAIGNS} campaigns at a time.` : ''}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', borderBottom: '1px solid #F0F0F0', pr: '8px' }}>
                        <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={disabled}
                            onChange={() => toggleCampaign(campaign)}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Typography noWrap sx={rowStyle}>{campaign.name}</Typography>
                            <Typography noWrap sx={secondaryStyle}>
                                {[campaign.position, campaign.type].filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                    </Box>
                </Tooltip>
            </div>
        );
    };

    const clearTooltip = hasActiveFilter
        ? `Clear ${clearableSelectedCount} selected campaign(s) from current filter`
        : 'Clear all selected campaigns';

    return (
        <OZDialog
            open={open}
            title="Delete Campaign Data"
            onClose={handleClose}
            hideActions
            maxWidth="md"
        >
            <Box sx={{ width: 'min(720px, 86vw)', maxWidth: '100%' }}>
                <Typography sx={{ fontSize: '12px', color: '#99A0A8', mb: '6px' }}>
                    Select the completed campaigns whose data you want to delete.
                </Typography>

                {availableTypes.length > 1 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mb: '8px' }}>
                        <Typography sx={{ fontSize: '12px', color: '#99A0A8', mr: '8px' }}>Type:</Typography>
                        {availableTypes.map((type) => (
                            <FormControlLabel
                                key={type}
                                control={(
                                    <Checkbox
                                        size="small"
                                        checked={selectedTypes.includes(type)}
                                        onChange={() => toggleType(type)}
                                    />
                                )}
                                label={<Typography sx={{ fontSize: '13px' }}>{type}</Typography>}
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Tooltip title={allPageSelected ? 'Clear selected (current page)' : 'Select all (current page)'}>
                        <span>
                            <Checkbox
                                size="small"
                                checked={allPageSelected}
                                indeterminate={somePageSelected}
                                disabled={loading || pageCampaigns.length === 0}
                                onChange={toggleSelectAllPage}
                            />
                        </span>
                    </Tooltip>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name or type"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: '18px', color: '#99A0A8' }} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '8px', fontSize: '14px' },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: '12px', mb: '4px' }}>
                    <Typography sx={{ fontSize: '12px', color: '#536580' }}>
                        {filteredCampaigns.length} campaign(s)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {clearableSelectedCount > 0 && (
                            <Tooltip title={clearTooltip}>
                                <Typography
                                    component="button"
                                    type="button"
                                    onClick={clearSelected}
                                    disabled={deleting}
                                    sx={{
                                        fontSize: '12px',
                                        color: '#3D8BF8',
                                        cursor: deleting ? 'not-allowed' : 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        fontFamily: 'inherit',
                                        opacity: deleting ? 0.5 : 1,
                                    }}
                                >
                                    Clear{hasActiveFilter ? ' filtered' : ''} ({clearableSelectedCount})
                                </Typography>
                            </Tooltip>
                        )}
                        {selectedCount > 0 && clearableSelectedCount === 0 && (
                            <Tooltip title={`Clear all ${selectedCount} selected campaign(s)`}>
                                <Typography
                                    component="button"
                                    type="button"
                                    onClick={clearAllSelected}
                                    disabled={deleting}
                                    sx={{
                                        fontSize: '12px',
                                        color: '#E65B5C',
                                        cursor: deleting ? 'not-allowed' : 'pointer',
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        fontFamily: 'inherit',
                                        opacity: deleting ? 0.5 : 1,
                                    }}
                                >
                                    Clear all ({selectedCount})
                                </Typography>
                            </Tooltip>
                        )}
                        {selectedCount > 0 && availableTypes.map((type) => (
                            <Tooltip key={type} title={`${type} selected`}>
                                <Typography sx={{ fontSize: '11px', color: '#212121', fontWeight: 700 }}>
                                    {TYPE_ABBR[type] || type}: {selectedTypeCounts[type] || 0}
                                </Typography>
                            </Tooltip>
                        ))}
                        <Typography sx={{ fontSize: '12px', color: capReached ? '#E65B5C' : '#536580' }}>
                            {selectedCount} / {BULK_DELETE_MAX_CAMPAIGNS} selected
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ border: '1px solid #E6E5E6', borderRadius: '8px', height: `${listBoxHeight}px`, overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : filteredCampaigns.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography sx={{ fontSize: '14px', color: '#99A0A8' }}>No eligible campaigns found.</Typography>
                        </Box>
                    ) : (
                        <List height={listHeight} itemCount={pageCampaigns.length} itemSize={ROW_HEIGHT} width="100%">
                            {renderRow}
                        </List>
                    )}
                </Box>

                {!loading && filteredCampaigns.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mt: '8px',
                            flexWrap: 'wrap',
                            gap: '8px',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => setRowsPerPageMenuAnchor(e.currentTarget)}
                                endIcon={<KeyboardArrowDownOutlinedIcon sx={{ color: '#546881' }} />}
                                sx={{
                                    borderRadius: '12px',
                                    borderColor: '#546881',
                                    color: '#000000',
                                    fontSize: '12px',
                                    minWidth: '103px',
                                    minHeight: '35px',
                                    textTransform: 'none',
                                }}
                            >
                                <Typography component="span" color="#546881" fontSize="12px" fontWeight="700">
                                    {rowsPerPage}
                                </Typography>
                                <Typography component="span" color="#000000" fontSize="12px" fontWeight="400" ml={0.5}>
                                    / Page
                                </Typography>
                            </Button>
                            <Menu
                                anchorEl={rowsPerPageMenuAnchor}
                                open={Boolean(rowsPerPageMenuAnchor)}
                                onClose={() => setRowsPerPageMenuAnchor(null)}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            mt: 0.5,
                                            borderRadius: '12px',
                                            border: '1px solid #546881',
                                            minWidth: '110px',
                                        },
                                    },
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <MenuItem
                                        key={size}
                                        value={size}
                                        onClick={handlePageSizeChange}
                                        sx={{
                                            fontSize: '13px',
                                            color: '#546881',
                                            borderTop: size !== PAGE_SIZE_OPTIONS[0] ? '1px solid rgba(0,0,0,0.1)' : 'none',
                                        }}
                                    >
                                        {size}
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Typography sx={{ fontSize: '12px', color: '#536580' }}>
                                Page {page} of {totalPages}
                            </Typography>
                        </Box>
                        {totalPages > 1 && (
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handleChangePage}
                                variant="outlined"
                                shape="circular"
                                size="small"
                                sx={{
                                    '& .MuiPaginationItem-previousNext': { color: '#3D8BF8', border: 'none' },
                                    '& .MuiPaginationItem-previousNext.Mui-disabled': { color: '#7A7A7A' },
                                    '& .MuiButtonBase-root.MuiPaginationItem-root.Mui-selected': {
                                        backgroundColor: '#E6F4FF !important',
                                    },
                                }}
                                renderItem={(item) => <PaginationRenderIcon item={item} />}
                            />
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', mt: '20px' }}>
                    <Button
                        variant="outlined"
                        sx={{ borderRadius: '8px', height: '44px', width: '91px', fontWeight: 400, color: '#727272', borderColor: '#727272' }}
                        onClick={handleClose}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: '8px', height: '44px', fontWeight: 400 }}
                        disabled={selectedCount === 0 || deleting}
                        onClick={() => setConfirmOpen(true)}
                    >
                        {deleting ? 'Deleting...' : `Delete${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
                    </Button>
                </Box>
            </Box>

            {confirmOpen && (
                <DeleteDialog
                    open
                    title="Delete Campaign Data"
                    content={`This will delete data for ${selectedCount} selected campaign(s) permanently. You cannot undo this action.`}
                    onClose={() => setConfirmOpen(false)}
                    deleteFunction={handleDelete}
                />
            )}
        </OZDialog>
    );
};

export default DeleteCampaignDataDialog;
